import axiosInstance from "@/api/axiosInstance";
import type { CourseStatus } from "@/modules/learningCenter/types/course";

export type { CourseStatus };

export interface Course {
  id: number;
  title: string;
  tagline: string | null;
  description: string;
  difficulty_level: "beginner" | "intermediate" | "advanced";
  estimated_hours: string | null;
  thumbnail_path: string | null;
  hero_banner_path: string | null;
  /** null = global (CertainPath library) course; otherwise the owning company. */
  company_id?: number | null;
  scope?: "global" | "company";
  archived: boolean;
  archived_at: string | null;
  status?: CourseStatus;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
  skills: Array<{
    id: number;
    title: string;
  }>;
  categories: Array<{
    id: number;
    name: string;
  }>;
  enrollment_count: number;
  module_count: number;
  enrollment?: {
    status: "not_started" | "in_progress" | "completed";
    progressPercentage: number;
  } | null;
}

export interface GetCoursesParams {
  page?: number;
  limit?: number;
  tradeId?: number;
  skillId?: number;
  categoryId?: number;
  difficultyLevel?: "beginner" | "intermediate" | "advanced";
  hasQuiz?: boolean;
  search?: string;
  includeArchived?: boolean;
  status?: CourseStatus;
}

export interface GetCoursesResponse {
  courses: Course[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

/**
 * Fetch paginated list of courses with optional filters
 * GET /api/learning-center/courses
 */
export const getCourses = async (
  params?: GetCoursesParams,
): Promise<GetCoursesResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.tradeId) queryParams.append("tradeId", params.tradeId.toString());
  if (params?.skillId) queryParams.append("skillId", params.skillId.toString());
  if (params?.categoryId)
    queryParams.append("categoryId", params.categoryId.toString());
  if (params?.difficultyLevel)
    queryParams.append("difficultyLevel", params.difficultyLevel);
  if (params?.hasQuiz !== undefined)
    queryParams.append("hasQuiz", params.hasQuiz.toString());
  if (params?.search) queryParams.append("search", params.search);
  if (params?.includeArchived) queryParams.append("includeArchived", "true");
  if (params?.status) queryParams.append("status", params.status);

  const response = await axiosInstance.get<GetCoursesResponse>(
    `/api/learning-center/courses?${queryParams.toString()}`,
  );

  return response.data;
};
